

import { Injectable, computed, signal } from '@angular/core';

import {
  HISTORY_COALESCE_MS,
  HISTORY_LIMIT,
  SCREEN_CONTENT_PADDING,
  SCREEN_FRAME_HEIGHT,
} from '../design-builder.constants';
import type {
  DesignDocument,
  DesignLayerKind,
  DesignNode,
  DesignPage,
  Rect,
} from '../design-builder.types';
import {
  createLayer,
  defaultIndexFor,
  locateLayer,
  memberLeavesOf,
  nodesByLayer,
  outermostGroupOf as resolveOutermostGroup,
  syncPageGroups,
  syncScreenLayers,
} from '../utils/design-layer.utils';
import { createEmptyDocument, slugify } from '../utils/design-json.utils';
import {
  type ZOrderCommand,
  absoluteOrigin,
  ancestorsOf,
  changeZOrder,
  duplicateNode,
  findNode,
  flattenNodes,
  groupNodes,
  insertNode,
  locateNode,
  moveNode,
  patchNode,
  removeNodes,
  ungroupNode,
  unionBounds,
} from '../utils/design-node.utils';
import { absoluteRect, roundGeometry } from '../utils/design-geometry.utils';
import { popupScreenIdsOf } from '../utils/design-publish.utils';

interface HistoryEntry {
  document: DesignDocument;
  label: string;
  at: number;
}

/** Scale a cloned subtree from the gesture-start subtree. Looking nodes up by
 * id keeps the operation safe if child ordering changes in a future model. */
function scaleChildGeometry(
  targets: DesignNode[],
  sources: readonly DesignNode[],
  scaleX: number,
  scaleY: number,
  scaleText = false,
): void {
  const sourceById = new Map(sources.map((node) => [node.id, node]));
  for (const target of targets) {
    const source = sourceById.get(target.id);
    if (!source) continue;
    target.x = roundGeometry(source.x * scaleX);
    target.y = roundGeometry(source.y * scaleY);
    target.width = Math.max(1, roundGeometry(source.width * scaleX));
    target.height = Math.max(1, roundGeometry(source.height * scaleY));
    target.groupScaleX = (source.groupScaleX ?? 1) * scaleX;
    target.groupScaleY = (source.groupScaleY ?? 1) * scaleY;
    // Popup screens keep their look: text grows / shrinks with the box, by the
    // tighter axis so it still fits.
    if (scaleText && target.text && source.text) {
      target.text = { ...target.text, fontSize: Math.max(1, Math.round(source.text.fontSize * Math.min(scaleX, scaleY) * 10) / 10) };
    }
    if (target.children?.length && source.children?.length) {
      scaleChildGeometry(target.children, source.children, scaleX, scaleY, scaleText);
    }
  }
}

@Injectable()
export class DesignDocumentStore {
  // ── Core state ─────────────────────────────────────────────────────────────
  private readonly _document = signal<DesignDocument>(createEmptyDocument());
  private readonly _activePageId = signal<string>('');
  private readonly _selectedIds = signal<ReadonlySet<string>>(new Set());
  private readonly _undo = signal<HistoryEntry[]>([]);
  private readonly _redo = signal<HistoryEntry[]>([]);
  /** Bumped on every commit — the JSON pane and autosave watch this. */
  private readonly _revision = signal(0);

  readonly document = this._document.asReadonly();
  readonly selectedIds = this._selectedIds.asReadonly();
  readonly revision = this._revision.asReadonly();

  readonly canUndo = computed(() => this._undo().length > 0);
  readonly canRedo = computed(() => this._redo().length > 0);

  readonly pages = computed<DesignPage[]>(() => this._document().pages);

  readonly activePage = computed<DesignPage>(() => {
    const pages = this._document().pages;
    return pages.find((page) => page.id === this._activePageId()) ?? pages[0];
  });

  readonly nodes = computed<DesignNode[]>(() => this.activePage()?.nodes ?? []);

  readonly selectedNodes = computed<DesignNode[]>(() => {
    const ids = this._selectedIds();
    if (!ids.size) return [];
    return flattenNodes(this.nodes()).filter((node) => ids.has(node.id));
  });

  /** The single selected node, or null when the selection is empty or multiple. */
  readonly selectedNode = computed<DesignNode | null>(() => {
    const selected = this.selectedNodes();
    return selected.length === 1 ? selected[0] : null;
  });

  readonly canvas = computed(() => this._document().canvas);

  /**
   * Root-level frames are SCREENS: the published page itself, not a layer.
   * {@link syncScreens} derives their box, so nothing may move or resize one.
   */
  readonly screenIds = computed<ReadonlySet<string>>(() => {
    // A button's popup screen is a free box, not the page: it moves and resizes.
    const popups = popupScreenIdsOf(this._document());
    return new Set(this.nodes().filter((node) => node.type === 'FRAME' && !popups.has(node.id)).map((node) => node.id));
  });

  isScreen(id: string): boolean {
    return this.screenIds().has(id);
  }

  // ── Layers ─────────────────────────────────────────────────────────────────

  /** Where a new node is filed. Empty = by the node's kind. */
  private readonly _activeLayerId = signal<string>('');
  readonly activeLayerId = this._activeLayerId.asReadonly();

  setActiveLayer(id: string): void {
    this._activeLayerId.set(id);
  }

  /** One band per kind: asking for a kind the screen already has just arms it. */
  addLayer(screenId: string, kind: DesignLayerKind): void {
    const frame = this.nodes().find((node) => node.id === screenId);
    const existing = frame?.layers?.find((layer) => layer.kind === kind);
    if (existing) {
      this._activeLayerId.set(existing.id);
      return;
    }
    const layer = createLayer(kind);
    this.commitNodes('Add layer', (nodes) => {
      const target = nodes.find((node) => node.id === screenId);
      if (!target) return;
      const layers = target.layers ?? (target.layers = []);
      layers.splice(defaultIndexFor(layers, kind), 0, layer);
    });
    this._activeLayerId.set(layer.id);
  }

  renameLayer(id: string, name: string): void {
    this.commitNodes('Rename layer', (nodes) => {
      const found = locateLayer(nodes, id);
      if (found) found.layer.name = name;
    });
  }

  /** Reorder a layer against another one — this is the screen's paint order. */
  moveLayerTo(dragId: string, targetId: string, position: 'before' | 'after'): void {
    this.commitNodes('Reorder layers', (nodes) => {
      const from = locateLayer(nodes, dragId);
      const to = locateLayer(nodes, targetId);
      if (!from || !to || from.frame !== to.frame) return;
      const layers = from.frame.layers!;
      layers.splice(from.index, 1);
      const target = layers.findIndex((layer) => layer.id === targetId);
      layers.splice(position === 'before' ? target : target + 1, 0, from.layer);
    });
  }

  /** Only an empty layer is removed — deleting content is the node's own job. */
  deleteLayer(id: string): void {
    this.commitNodes('Delete layer', (nodes) => {
      const found = locateLayer(nodes, id);
      if (!found) return;
      const used = (found.frame.children ?? []).some((child) => child.layerId === id);
      if (!used) found.frame.layers!.splice(found.index, 1);
    });
    if (this._activeLayerId() === id) this._activeLayerId.set('');
  }

  /**
   * Hide or lock a whole layer.
   *
   * Applied to its NODES rather than stored on the layer: the canvas, the
   * published renderer and the layers panel already honour `visible` / `locked`
   * per node, so there is no second flag anything could forget to check.
   */
  setLayerFlag(id: string, flag: 'visible' | 'locked', value: boolean): void {
    this.commitNodes(flag === 'visible' ? 'Hide layer' : 'Lock layer', (nodes) => {
      const found = locateLayer(nodes, id);
      if (!found) return;
      for (const child of found.frame.children ?? []) {
        if (child.layerId === id) child[flag] = value;
      }
    });
  }

  moveNodeToLayer(nodeId: string, layerId: string): void {
    this.commitNodes('Move to layer', (nodes) => {
      const found = locateLayer(nodes, layerId);
      const node = found?.frame.children?.find((child) => child.id === nodeId);
      // Only a direct child of that screen can change band; a nested node has
      // to be reparented out first, which is a different gesture.
      if (node) node.layerId = layerId;
    });
  }

  /** Select everything in a band — clicking its header picks the whole layer. */
  selectLayerNodes(layerId: string): void {
    const found = locateLayer(this.nodes(), layerId);
    if (!found) return;
    const ids = (found.frame.children ?? [])
      .filter((child) => child.layerId === layerId)
      .map((child) => child.id);
    this.selectMany(ids);
  }

  /** Nodes of each layer of a screen, for the panel. */
  layerNodes(screenId: string): Map<string, DesignNode[]> {
    const frame = this.nodes().find((node) => node.id === screenId);
    return frame ? nodesByLayer(frame) : new Map();
  }

  constructor() {
    this._activePageId.set(this._document().pages[0]?.id ?? '');
  }

  // ── Commit ─────────────────────────────────────────────────────────────────

  /**
   * Apply `mutate` to a deep clone of the document and publish it.
   *
   * Consecutive commits sharing a `label` within {@link HISTORY_COALESCE_MS}
   * collapse into one undo entry, so a drag is one history step rather than one
   * per pointermove.
   */
  commit(label: string, mutate: (document: DesignDocument) => void): void {
    const previous = this._document();
    const next = structuredCloneDocument(previous);
    mutate(next);
    // Deriving the screens on the one commit path is what makes their box
    // unsettable: any width or height a caller wrote is overwritten here.
    this.syncScreens(next);
    next.meta.updatedAt = new Date().toISOString();

    const history = this._undo();
    const last = history[history.length - 1];
    const coalesce = !!last && last.label === label && Date.now() - last.at < HISTORY_COALESCE_MS;

    if (coalesce) {
      // Keep the ORIGINAL pre-drag snapshot; only refresh the coalescing clock.
      this._undo.set([...history.slice(0, -1), { ...last, at: Date.now() }]);
    } else {
      const entry: HistoryEntry = { document: previous, label, at: Date.now() };
      this._undo.set([...history, entry].slice(-HISTORY_LIMIT));
    }

    this._redo.set([]);
    this._document.set(next);
    this._revision.update((value) => value + 1);
  }

  /** Replace the whole document (load, or JSON paste-and-apply) as one step. */
  setDocument(document: DesignDocument, label = 'Load design'): void {
    const previous = this._document();
    this._undo.set([...this._undo(), { document: previous, label, at: Date.now() }].slice(-HISTORY_LIMIT));
    this._redo.set([]);
    this.syncScreens(document);
    this._document.set(document);
    this._activePageId.set(document.pages[0]?.id ?? '');
    this._selectedIds.set(new Set());
    this._revision.update((value) => value + 1);
  }

  /** Load without touching history — used when opening a saved design. */
  resetDocument(document: DesignDocument): void {
    this._undo.set([]);
    this._redo.set([]);
    // Designs saved before the screen box was derived are brought in line here.
    this.syncScreens(document);
    this._document.set(document);
    this._activePageId.set(document.pages[0]?.id ?? '');
    this._selectedIds.set(new Set());
    this._revision.update((value) => value + 1);
  }

  undo(): void {
    const history = this._undo();
    if (!history.length) return;
    const entry = history[history.length - 1];
    this._undo.set(history.slice(0, -1));
    this._redo.set([...this._redo(), { document: this._document(), label: entry.label, at: Date.now() }]);
    this._document.set(entry.document);
    this.pruneSelection();
    this._revision.update((value) => value + 1);
  }

  redo(): void {
    const stack = this._redo();
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    this._redo.set(stack.slice(0, -1));
    this._undo.set([...this._undo(), { document: this._document(), label: entry.label, at: Date.now() }]);
    this._document.set(entry.document);
    this.pruneSelection();
    this._revision.update((value) => value + 1);
  }

  /** Drop selected ids that no longer exist (after undo past a delete). */
  private pruneSelection(): void {
    const live = new Set(flattenNodes(this.nodes()).map((node) => node.id));
    const next = new Set([...this._selectedIds()].filter((id) => live.has(id)));
    if (next.size !== this._selectedIds().size) this._selectedIds.set(next);
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  select(id: string | null): void {
    this._selectedIds.set(id ? new Set([id]) : new Set());
  }

  selectMany(ids: Iterable<string>): void {
    this._selectedIds.set(new Set(ids));
  }

  toggleSelected(id: string): void {
    const next = new Set(this._selectedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._selectedIds.set(next);
  }

  clearSelection(): void {
    if (this._selectedIds().size) this._selectedIds.set(new Set());
  }

  selectAll(): void {
    this._selectedIds.set(new Set(this.nodes().map((node) => node.id)));
  }

  isSelected(id: string): boolean {
    return this._selectedIds().has(id);
  }

  // ── Pages ──────────────────────────────────────────────────────────────────

  setActivePage(id: string): void {
    this._activePageId.set(id);
    this.clearSelection();
  }

  addPage(name?: string): void {
    this.commit('Add page', (document) => {
      const page: DesignPage = {
        id: `pg_${Date.now().toString(36)}`,
        name: name || `Page ${document.pages.length + 1}`,
        nodes: [],
      };
      document.pages.push(page);
      this._activePageId.set(page.id);
    });
  }

  removePage(id: string): void {
    if (this._document().pages.length <= 1) return;
    this.commit('Remove page', (document) => {
      document.pages = document.pages.filter((page) => page.id !== id);
      if (this._activePageId() === id) this._activePageId.set(document.pages[0].id);
    });
  }

  // ── Node mutations ─────────────────────────────────────────────────────────

  /** Run a mutator against the ACTIVE page's node array. */
  private commitNodes(label: string, mutate: (nodes: DesignNode[], document: DesignDocument) => void): void {
    const pageId = this.activePage()?.id;
    this.commit(label, (document) => {
      const page = document.pages.find((candidate) => candidate.id === pageId) ?? document.pages[0];
      if (page) mutate(page.nodes, document);
    });
  }

  addNode(node: DesignNode, parentId: string | null = null, index?: number): void {
    this.commitNodes('Add node', (nodes, document) => {
      // A layer picked in the panel wins; otherwise the sync files the node by
      // its kind. An id that does not belong to this screen is dropped there.
      if (this._activeLayerId()) node.layerId = this._activeLayerId();
      insertNode(nodes, node, parentId, index);
      // Root-level only — a nested node's coordinates are relative to its
      // parent, not the artboard.
      if (!parentId) this.growCanvasToFit(document, node);
    });
    this.select(node.id);
  }

  /** Extend the artboard so `node` fits inside it, with a little breathing room. */
  private growCanvasToFit(document: DesignDocument, node: DesignNode): void {
    const bottom = node.y + node.height;
    if (!node.fluid) {
      const right = node.x + node.width;
      if (right > document.canvas.width) document.canvas.width = Math.ceil(right + ARTBOARD_MARGIN);
    }
    if (bottom > document.canvas.height) {
      document.canvas.height = Math.ceil(bottom + ARTBOARD_MARGIN);
    }
  }

  /**
   * Derive every screen's box: the width from the artboard (content scales with
   * it, so a design laid out on a laptop fills a wider monitor), the height
   * from the lowest element — which is how a long, scrolling page is made.
   *
   * An auto-layout screen keeps its height: its children flow, so their stored
   * boxes do not say where the content ends — the renderer measures that.
   */
  private syncScreens(document: DesignDocument): void {
    let lowest = 0;
    const popups = popupScreenIdsOf(document);
    for (const page of document.pages) {
      for (const node of page.nodes) {
        if (node.type !== 'FRAME') continue;
        // Popup screens keep the box their designer gave them.
        if (popups.has(node.id)) {
          delete node.fluid;
          if (node.visible !== false) lowest = Math.max(lowest, node.y + node.height);
          continue;
        }
        const previousWidth = node.width;
        node.fluid = true;
        node.x = 0;
        node.width = document.canvas.width;

        const flowing = !!node.layout?.mode && node.layout.mode !== 'none';
        if (!flowing) {
          // The layout adjusts to the new width: every layer keeps its place
          // RELATIVE to the page, so narrowing the canvas never hides the right
          // side. The W boxes apply on Enter / blur, so this runs once per
          // resize — never through every keystroke (2 → 20 → 200 → 2006).
          if (previousWidth > 0 && previousWidth !== node.width) {
            const scale = node.width / previousWidth;
            node.children?.forEach((child) => scaleNodeHorizontally(child, scale));
          }
          // A height the author set is the floor; the page still grows to fit its content.
          node.height = Math.max(
            node.screenSize?.height ?? SCREEN_FRAME_HEIGHT,
            Math.ceil(contentBottom(node.children ?? []) + SCREEN_CONTENT_PADDING),
          );
        }
        syncScreenLayers(node);
        lowest = Math.max(lowest, node.y + node.height);
      }
    }
    // Groups made of page-level layers (outside any screen) are kept in step
    // with their members too — the screens' own groups are synced above.
    for (const page of document.pages) syncPageGroups(page.nodes);
    // The artboard contains the screens, so it can never be shorter than them.
    if (lowest > document.canvas.height) {
      document.canvas.height = Math.ceil(lowest + ARTBOARD_MARGIN);
    }
  }

  deleteSelected(): void {
    // A screen is the page and cannot be drawn again, so the LAST one is never
    // deleted; any extra screen (each publishes as its own tab) can be.
    const selected = [...this._selectedIds()];
    const screens = [...this.screenIds()];
    const keep = screens.length && screens.every((id) => selected.includes(id)) ? screens[0] : null;
    const ids = selected.filter((id) => id !== keep);
    if (!ids.length) return;
    this.commitNodes('Delete', (nodes) => removeNodes(nodes, ids));
    this.clearSelection();
  }

  duplicateSelected(): void {
    const ids = [...this._selectedIds()];
    if (!ids.length) return;
    const created: string[] = [];
    this.commitNodes('Duplicate', (nodes) => {
      const copied = new Set<string>();
      // Groups first: a group is a reference, so its copy needs copies of the
      // members to point at — and those must not then be duplicated again.
      for (const id of [...ids].sort((a, b) => groupFirst(nodes, a, b))) {
        const location = locateNode(nodes, id);
        if (!location || copied.has(id)) continue;
        const node = location.node;

        if (!node.memberIds?.length) {
          const copy = duplicateNode(node);
          location.siblings.splice(location.index + 1, 0, copy);
          copied.add(id);
          created.push(copy.id);
          continue;
        }

        const remapped: string[] = [];
        for (const memberId of node.memberIds) {
          const member = locateNode(nodes, memberId);
          if (!member) continue;
          const copy = duplicateNode(member.node);
          member.siblings.splice(member.index + 1, 0, copy);
          copied.add(memberId);
          remapped.push(copy.id);
        }
        const groupCopy = duplicateNode(node);
        groupCopy.memberIds = remapped;
        location.siblings.push(groupCopy);
        copied.add(id);
        created.push(groupCopy.id);
      }
    });
    if (created.length) this.selectMany(created);
  }

  updateNode(id: string, patch: Partial<DesignNode>, label = 'Update node'): void {
    this.commitNodes(label, (nodes) => patchNode(nodes, id, patch));
  }

  /** Same patch across the whole selection — how the inspector edits multi-selects. */
  updateSelected(patch: Partial<DesignNode>, label = 'Update nodes'): void {
    const ids = [...this._selectedIds()];
    if (!ids.length) return;
    this.commitNodes(label, (nodes) => ids.forEach((id) => patchNode(nodes, id, patch)));
  }

  /** Geometry during a drag/resize — labelled so the whole gesture coalesces. */
  setNodeRect(id: string, rect: Partial<Rect>, label = 'Move'): void {
    this.commitNodes(label, (nodes) => patchNode(nodes, id, rect as Partial<DesignNode>));
  }

  /**
   * Resize a node from a stable snapshot. GROUP children scale with the group,
   * retaining their relative positions and dimensions. Pointer gestures pass
   * the pointer-down snapshot to prevent cumulative rounding drift.
   */
  resizeNode(id: string, rect: Rect, source?: DesignNode, label = 'Resize'): void {
    this.commitNodes(label, (nodes, document) => {
      const target = findNode(nodes, id);
      if (!target) return;

      const basis = source ?? target;
      const scaleX = basis.width ? rect.width / basis.width : 1;
      const scaleY = basis.height ? rect.height / basis.height : 1;

      target.x = roundGeometry(rect.x);
      target.y = roundGeometry(rect.y);
      target.width = Math.max(1, roundGeometry(rect.width));
      target.height = Math.max(1, roundGeometry(rect.height));

      if (target.type === 'GROUP' && target.children?.length && basis.children?.length) {
        scaleChildGeometry(target.children, basis.children, scaleX, scaleY);
      }
      // A free-positioned popup screen: everything inside rescales with it.
      if (this.isScalingPopupFrame(target, document) && target.children?.length && basis.children?.length) {
        scaleChildGeometry(target.children, basis.children, scaleX, scaleY, true);
      }
    });
  }


  /**
   * Resize a group by scaling its MEMBERS, which are siblings of it rather than
   * its children. `starts` holds the pre-gesture snapshot of the group and of
   * every member, so a long drag cannot accumulate rounding drift; the group's
   * own box is re-derived from the result on commit.
   */
  resizeGroup(id: string, rect: Rect, starts: ReadonlyMap<string, DesignNode>, label = 'Resize'): void {
    this.commitNodes(label, (nodes) => {
      const group = findNode(nodes, id);
      const basis = starts.get(id) ?? group;
      if (!group || !basis) return;

      const scaleX = basis.width ? rect.width / basis.width : 1;
      const scaleY = basis.height ? rect.height / basis.height : 1;

      for (const [memberId, start] of starts) {
        if (memberId === id) continue;
        const member = findNode(nodes, memberId);
        if (!member || member.locked) continue;
        member.x = roundGeometry(rect.x + (start.x - basis.x) * scaleX);
        member.y = roundGeometry(rect.y + (start.y - basis.y) * scaleY);
        member.width = Math.max(1, roundGeometry(start.width * scaleX));
        member.height = Math.max(1, roundGeometry(start.height * scaleY));
        // Fixed-size widget internals scale with the composition instead of
        // overflowing their new box — the same metadata a GROUP resize used.
        member.groupScaleX = (start.groupScaleX ?? 1) * scaleX;
        member.groupScaleY = (start.groupScaleY ?? 1) * scaleY;
      }
    });
  }

  /**
   * The outermost group holding this node, if any.
   *
   * A group draws nothing, so a click on the canvas always lands on a MEMBER.
   * Resolving it to the group here is what makes a group behave like one thing
   * — select it, drag it, and everything in it comes along.
   */
  outermostGroupOf(id: string): DesignNode | null {
    return resolveOutermostGroup(flattenNodes(this.nodes()), id);
  }

  /** The nodes a group ends at, following nested groups down. */
  memberLeaves(node: DesignNode): DesignNode[] {
    if (!node.memberIds?.length) return [node];
    // Every layer, page level included: searching only inside the screens left a
    // group of loose page items with no members, so it could not be dragged.
    const byId = new Map(flattenNodes(this.nodes()).map((item) => [item.id, item] as const));
    return memberLeavesOf(node, byId);
  }

  /** A root frame some button opens as a popup, laid out freely (no auto-layout). */
  isPopupFrame(id: string): boolean {
    const node = findNode(this.nodes(), id);
    return !!node && this.isScalingPopupFrame(node, this._document());
  }

  private isScalingPopupFrame(node: DesignNode, document: DesignDocument): boolean {
    return node.type === 'FRAME'
      && (!node.layout || node.layout.mode === 'none')
      && popupScreenIdsOf(document).has(node.id);

  }

  /** Move several nodes by the same delta (multi-select drag). */
  nudgeSelected(dx: number, dy: number, label = 'Move'): void {
    const ids = [...this._selectedIds()];
    if (!ids.length) return;
    this.commitNodes(label, (nodes) => {
      for (const id of ids) {
        const node = findNode(nodes, id);
        // A screen is the page: arrow keys nudge what is ON it, never it.
        if (!node || node.locked || this.isScreen(id)) continue;
        node.x = roundGeometry(node.x + dx);
        node.y = roundGeometry(node.y + dy);
      }
    });
  }

  /**
   * Move a node under a new parent, keeping it where it LOOKS.
   *
   * Coordinates are parent-relative, so a bare move would teleport the node by
   * the difference between the two parents' origins. Capture its absolute
   * position first and rebase into the new parent afterwards.
   */
  reparent(id: string, parentId: string | null, index?: number): void {
    // A screen may be reordered among its siblings but never nested in a layer.
    const target = this.isScreen(id) ? null : parentId;
    this.commitNodes('Reparent', (nodes) => {
      const before = absoluteOrigin(nodes, id);
      if (!moveNode(nodes, id, target, index) || !before) return;

      const parentOrigin = target ? absoluteOrigin(nodes, target) : { x: 0, y: 0 };
      const node = findNode(nodes, id);
      if (!node || !parentOrigin) return;
      node.x = roundGeometry(before.x - parentOrigin.x);
      node.y = roundGeometry(before.y - parentOrigin.y);
      inheritNeighbourLayer(nodes, id);
    });
  }

  reorder(id: string, command: ZOrderCommand): void {
    this.commitNodes('Reorder', (nodes) => {
      changeZOrder(nodes, id, command);
      inheritNeighbourLayer(nodes, id);
    });
  }

  groupSelected(): void {
    // A screen is the page itself — grouping it would drag the whole page.
    const ids = [...this._selectedIds()].filter((id) => !this.isScreen(id));
    if (ids.length < 2) return;
    let groupId: string | null = null;
    this.commitNodes('Group', (nodes) => {
      groupId = groupNodes(nodes, ids)?.id ?? null;
    });
    if (groupId) this.select(groupId);
  }

  ungroupSelected(): void {
    // A member picked on its own (Ctrl/Cmd-click) ungroups the group it is in.
    const ids = [...new Set([...this._selectedIds()].map((id) => {
      const node = findNode(this.nodes(), id);
      return node?.memberIds?.length ? id : this.outermostGroupOf(id)?.id ?? id;
    }))];
    if (!ids.length) return;
    const freed: string[] = [];
    this.commitNodes('Ungroup', (nodes) => {
      for (const id of ids) {
        const node = findNode(nodes, id);
        if (!node?.memberIds?.length) continue;
        freed.push(...ungroupNode(nodes, id).map((member) => member.id));
      }
    });
    if (freed.length) this.selectMany(freed);
  }

  toggleVisible(id: string): void {
    const node = findNode(this.nodes(), id);
    if (node) this.updateNode(id, { visible: !node.visible }, 'Toggle visibility');
  }

  toggleLocked(id: string): void {
    const node = findNode(this.nodes(), id);
    if (node) this.updateNode(id, { locked: !node.locked }, 'Toggle lock');
  }

  rename(id: string, name: string): void {
    this.updateNode(id, { name }, 'Rename');
  }

  // ── Document settings ──────────────────────────────────────────────────────

  setName(name: string): void {
    this.commit('Rename design', (document) => {
      document.name = name;
      document.slug = slugify(name);
    });
  }

  /** File this design under a project (the parent that groups dashboards). */
  setProject(projectId: string, projectName: string): void {
    this.commit('Project', (document) => {
      document.projectId = projectId || undefined;
      document.projectName = projectId ? projectName : undefined;
    });
  }

  /** Resizing the artboard resizes the screens with it — see `syncScreens`. */
  /**
   * The Screen panel's W / H. `null` hands that side back to automatic.
   *
   * The width is the artboard's (every screen follows it in
   * {@link syncScreens}); like any canvas resize, the layout adjusts to it.
   */
  resizeScreen(id: string, size: { width?: number | null; height?: number | null }): void {
    this.commit('Screen size', (document) => {
      const frame = document.pages.flatMap((page) => page.nodes).find((node) => node.id === id && node.type === 'FRAME');
      if (!frame) return;
      const next = { ...(frame.screenSize ?? {}) };
      (['width', 'height'] as const).forEach((side) => {
        const value = size[side];
        if (value === undefined) return;
        if (value === null) delete next[side];
        else next[side] = Math.round(value);
      });
      if (Object.keys(next).length) frame.screenSize = next;
      else delete frame.screenSize;

      if (typeof size.width === 'number') document.canvas.width = Math.round(size.width);

      // Height works like width: the layout adjusts to it, every layer keeping
      // its place relative to the page. Scaled against the space ABOVE the
      // bottom padding, so the content ends exactly that padding above the new
      // height and the page lands on the number typed (never pushed taller).
      const flowing = !!frame.layout?.mode && frame.layout.mode !== 'none';
      if (typeof size.height === 'number' && !flowing && frame.height > SCREEN_CONTENT_PADDING) {
        const target = Math.round(size.height);
        const scale = (target - SCREEN_CONTENT_PADDING) / (frame.height - SCREEN_CONTENT_PADDING);
        if (scale > 0 && scale !== 1) frame.children?.forEach((child) => scaleNodeVertically(child, scale));
      }
    });
  }

  patchCanvas(patch: Partial<DesignDocument['canvas']>): void {
    this.commit('Canvas settings', (document) => {
      Object.assign(document.canvas, patch);
    });
  }

  setGrid(patch: Partial<DesignDocument['canvas']['grid']>): void {
    this.commit('Grid settings', (document) => {
      Object.assign(document.canvas.grid, patch);
    });
  }

  setStatus(status: DesignDocument['meta']['status']): void {
    this.commit('Status', (document) => {
      document.meta.status = status;
      if (status === 'published') document.meta.publishedAt = new Date().toISOString();
    });
  }

  // ── Derived geometry ───────────────────────────────────────────────────────

  /** A node's rect in ROOT document space (what the canvas overlay needs). */
  absoluteRectOf(id: string): Rect | null {
    const nodes = this.nodes();
    const node = findNode(nodes, id);
    if (!node) return null;
    return absoluteRect(node, ancestorsOf(nodes, id));
  }

  /** Bounding box of the whole selection, in root document space. */
  selectionBounds(): Rect | null {
    const rects = [...this._selectedIds()]
      .map((id) => this.absoluteRectOf(id))
      .filter((rect): rect is Rect => !!rect);
    if (!rects.length) return null;
    return unionBounds(rects);
  }

  /** Bounding box of every root node — used by "zoom to fit". */
  contentBounds(): Rect {
    const nodes = this.nodes();
    if (!nodes.length) {
      const canvas = this.canvas();
      return { x: 0, y: 0, width: canvas.width, height: canvas.height };
    }
    return unionBounds(nodes);
  }
}

/** Breathing room kept between the outermost content and the artboard edge. */
const ARTBOARD_MARGIN = 40;

/** Sort comparator that puts groups ahead of plain nodes. */
function groupFirst(nodes: DesignNode[], a: string, b: string): number {
  const weight = (id: string): number => (findNode(nodes, id)?.memberIds?.length ? 0 : 1);
  return weight(a) - weight(b);
}

/**
 * A node that has just been moved among a screen's children joins the band of
 * whichever node it landed next to.
 *
 * Without this, layers would make z-order edits look broken: the children array
 * is re-sorted by layer on every commit, so dragging a node past a band
 * boundary — or hitting bring-to-front — would snap straight back.
 */
function inheritNeighbourLayer(nodes: DesignNode[], id: string): void {
  const location = locateNode(nodes, id);
  if (!location?.parent?.layers?.length) return;
  const { siblings, index, node } = location;
  const neighbour = siblings[index - 1] ?? siblings[index + 1];
  if (neighbour?.layerId) node.layerId = neighbour.layerId;
}

/**
 * The lowest point anything reaches, children included — a group's children can
 * hang outside its stored box, and a screen must not cut them off.
 */
function contentBottom(nodes: readonly DesignNode[], offsetY = 0): number {
  let bottom = 0;
  for (const node of nodes) {
    if (node.visible === false) continue;
    const y = offsetY + node.y;
    bottom = Math.max(bottom, y + node.height);
    if (node.children?.length) bottom = Math.max(bottom, contentBottom(node.children, y));
  }
  return bottom;
}

/**
 * Resize a node and its subtree along X with the page. Icons keep their size
 * and only move, so a glyph never turns into a smear. Kept fractional:
 * rounding on every resize would make narrow → wide → narrow drift.
 */
function scaleNodeHorizontally(node: DesignNode, scale: number): void {
  if (node.type === 'ICON') {
    node.x = node.x * scale + (node.width * scale - node.width) / 2;
    return;
  }
  node.x = node.x * scale;
  node.width = node.width * scale;
  node.children?.forEach((child) => scaleNodeHorizontally(child, scale));
}

/** {@link scaleNodeHorizontally} along Y — the Screen panel's height. Icons keep their size and only move. */
function scaleNodeVertically(node: DesignNode, scale: number): void {
  if (node.type === 'ICON') {
    node.y = node.y * scale + (node.height * scale - node.height) / 2;
    return;
  }
  node.y = node.y * scale;
  node.height = Math.max(1, node.height * scale);
  node.children?.forEach((child) => scaleNodeVertically(child, scale));
}

/** `structuredClone` where available, JSON round-trip otherwise (older Safari). */
function structuredCloneDocument(document: DesignDocument): DesignDocument {
  const clone = (globalThis as { structuredClone?: <T>(value: T) => T }).structuredClone;
  return clone ? clone(document) : (JSON.parse(JSON.stringify(document)) as DesignDocument);
}
